import Badge from '@/app/common/components/Badge'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import PageLoader from '@/app/common/components/PageLoader'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shad/ui/table'
import { ArrowDownCircle, ArrowUpCircle, Calendar } from 'lucide-react'

import Pagination from '@/app/common/components/Pagination'
import { usePagination } from '@/app/common/usePagination'
import { parseRequestError } from '@/app/common/utilities'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useSelector } from 'react-redux'
import { z } from 'zod'
import { currencyFormatter } from '@/lib/utils'
import { PURSE_KEYS } from '@/queries/program_purses/keys.ts'
import { ProgramPurseResource } from '@/queries/program_purses/schemas.ts'

const TransactionLogSchema = z.object({
  id: z.number(),
  program_purse_id: z.number(),
  purse_id: z.number().nullable(),
  user_id: z.number(),
  type: z.number(),
  sender: z.string().nullable(),
  amount: z.number(),
  created_at: z.string().datetime(), // ISO 8601 datetime string
  student_id: z.number().nullable(),
  student_name: z.string().nullable(),
  grade: z.string().nullable(),
  type_name: z.string(),
  user_name: z.string(),
})

// Add TransactionLog type for type safety
export type TransactionLog = z.infer<typeof TransactionLogSchema>

// Response schema for paginated transaction logs
const TransactionLogsResponseSchema = z.object({
  data: z.array(TransactionLogSchema),
  current_page: z.number(),
  last_page: z.number(),
  per_page: z.number(),
  total: z.number(),
  total_incoming: z.number(),
  total_outgoing: z.number(),
})

export type TransactionLogsResponse = z.infer<
  typeof TransactionLogsResponseSchema
>

// API function to fetch transaction logs
// eslint-disable-next-line
export const getTransactionLogs = async ({
  programId,
  purseId,
  page,
  per_page,
}: {
  programId: number
  purseId: number
  page: number
  per_page: number
}): Promise<TransactionLogsResponse> => {
  try {
    const params: Record<string, string | number> = {
      page,
      per_page,
    }

    const response = await axios.get(
      `/programs/${programId}/purses/${purseId}/logs`,
      { params }
    )

    return TransactionLogsResponseSchema.parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

// Simple CardTitle component
const CardTitle = ({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) => <h3 className={`text-lg font-semibold ${className}`}>{children}</h3>

interface TransactionLogProps {
  purse: ProgramPurseResource
}

export function TransactionLog({ purse }: TransactionLogProps) {
  const { currentPage, setCurrentPage, perPage, setPerPage } = usePagination(20)

  const programId = useSelector(selectSelectedProgram).id

  // Use React Query to fetch transaction logs
  const transactionLogsQuery = useQuery({
    queryKey: PURSE_KEYS.TRANSACTION_LOGS({
      programId,
      purseId: purse.id,
      page: currentPage,
      perPage,
    }),
    queryFn: () =>
      getTransactionLogs({
        programId,
        purseId: purse.id,
        page: currentPage,
        per_page: perPage,
      }),
  })

  const allTransactions: TransactionLog[] =
    transactionLogsQuery.data?.data || []
  const totalRows = transactionLogsQuery.data?.total || 0

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  // Calculate totals for summary cards

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <ArrowUpCircle className="h-5 w-5 text-green-600" />
              <div>
                <div className="text-sm text-gray-600">Total Incoming</div>
                <div className="text-lg font-bold text-green-600">
                  {transactionLogsQuery.isLoading
                    ? '—'
                    : currencyFormatter.format(
                        transactionLogsQuery.data?.total_incoming || 0
                      )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <ArrowDownCircle className="h-5 w-5 text-red-600" />
              <div>
                <div className="text-sm text-gray-600">Total Outgoing</div>
                <div className="text-lg font-bold text-red-600">
                  {transactionLogsQuery.isLoading
                    ? '—'
                    : currencyFormatter.format(
                        transactionLogsQuery.data?.total_outgoing ?? 0
                      )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              <div>
                <div className="text-sm text-gray-600">Total Transactions</div>
                <div className="text-lg font-bold text-blue-600">
                  {transactionLogsQuery.isLoading ? '—' : totalRows}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transaction Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>
              Transaction History (
              {transactionLogsQuery.isLoading ? '—' : totalRows} transactions)
            </CardTitle>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              Showing{' '}
              {transactionLogsQuery.isLoading
                ? '—'
                : (currentPage - 1) * perPage + 1}
              -
              {transactionLogsQuery.isLoading
                ? '—'
                : Math.min(currentPage * perPage, totalRows)}{' '}
              of {transactionLogsQuery.isLoading ? '—' : totalRows}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {transactionLogsQuery.isLoading ? (
            <PageLoader query={transactionLogsQuery}>{() => null}</PageLoader>
          ) : allTransactions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No transactions found.</p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead>Performed By</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Reference</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allTransactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell className="font-medium">
                          {formatDate(transaction.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {transaction.type_name === 'Deposit' ||
                            transaction.type_name === 'Initial' ? (
                              <ArrowUpCircle className="h-4 w-4 text-green-600" />
                            ) : transaction.type_name === 'Distribution' ? (
                              <ArrowDownCircle className="h-4 w-4 text-red-600" />
                            ) : null}
                            <Badge
                              semanticColor={
                                transaction.type_name === 'Deposit' ||
                                transaction.type_name === 'Initial'
                                  ? 'Positive'
                                  : transaction.type_name === 'Distribution'
                                    ? 'Negative'
                                    : 'Neutral'
                              }
                            >
                              {transaction.type_name}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span
                            className={
                              transaction.type_name === 'Deposit' ||
                              transaction.type_name === 'Initial'
                                ? 'text-green-600 font-medium'
                                : transaction.type_name === 'Distribution'
                                  ? 'text-red-600 font-medium'
                                  : ''
                            }
                          >
                            {transaction.type_name === 'Deposit' ||
                            transaction.type_name === 'Initial'
                              ? '+'
                              : transaction.type_name === 'Distribution'
                                ? '-'
                                : ''}
                            {currencyFormatter.format(transaction.amount)}
                          </span>
                        </TableCell>
                        <TableCell>
                          {transaction.student_name ? (
                            <div>
                              <div className="font-medium">
                                {transaction.student_name}
                              </div>
                              {transaction.grade && (
                                <div className="text-xs text-gray-500">
                                  {transaction.grade}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {transaction.user_name}
                          </div>
                        </TableCell>
                        <TableCell>{transaction.sender || '—'}</TableCell>
                        <TableCell>
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {transaction.id}
                          </code>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <Pagination
                currentPage={currentPage}
                itemsPerPage={perPage}
                totalRows={totalRows}
                onPageChange={setCurrentPage}
                onPerPageChange={setPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
